import Checkbox from 'core/elements/input/Checkbox'
import Divider from 'core/elements/Divider'
import { UNASSIGNED_CLUSTER } from 'app/plugins/openstack/components/infrastructure/constants'
import React from 'react'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import colors from 'core/themes/base/colors'
import Theme from 'core/themes/model'

interface ClusterListProps {
  visibleClusters: any[]
  isUnassignedHostsSelected: boolean
  selectedClusterNames: Set<string>
  setIsUnassignedHostsSelected: (checked: boolean) => void
  setSelectedClusterNames: React.Dispatch<React.SetStateAction<Set<string>>>
  setSelectedHostIds: React.Dispatch<React.SetStateAction<Set<string>>>
  unassignedHosts: any[]
  clusterByNameMap: Map<string, any>
}

const ClusterList = ({
  visibleClusters,
  isUnassignedHostsSelected,
  selectedClusterNames,
  setIsUnassignedHostsSelected,
  setSelectedClusterNames,
  setSelectedHostIds,
  unassignedHosts,
  clusterByNameMap,
}: ClusterListProps) => {
  const classes = useStyles()

  const isClusterSelected = (clusterName: string) => {
    // Unassigned hosts are tracked separately from named clusters
    if (clusterName === UNASSIGNED_CLUSTER) {
      return isUnassignedHostsSelected
    }
    return selectedClusterNames.has(clusterName)
  }

  const handleClusterToggle = (clusterName: string, checked: boolean) => {
    // Unassigned hosts use separate state flag, regular clusters update both cluster and host selections
    if (clusterName === UNASSIGNED_CLUSTER) {
      setIsUnassignedHostsSelected(checked)
      setSelectedHostIds((prev: Set<string>) => {
        const newSet = new Set(prev)
        if (checked) {
          unassignedHosts.forEach((host) => newSet.add(host.id))
        } else {
          unassignedHosts.forEach((host) => newSet.delete(host.id))
        }
        return newSet
      })
    } else {
      const cluster = clusterByNameMap.get(clusterName)
      setSelectedClusterNames((prev: Set<string>) => {
        const newSet = new Set(prev)
        if (checked) {
          newSet.add(clusterName)
        } else {
          newSet.delete(clusterName)
        }
        return newSet
      })

      // Only update host selection if cluster has hosts
      if (cluster?.hostlist && cluster.hostlist.length > 0) {
        setSelectedHostIds((prev: Set<string>) => {
          const newSet = new Set(prev)
          if (checked) {
            cluster.hostlist.forEach((hostId) => newSet.add(hostId))
          } else {
            cluster.hostlist.forEach((hostId) => newSet.delete(hostId))
          }
          return newSet
        })
      }
    }
  }

  return (
    <div className={classes.listContainer}>
      {/* Unassigned Hosts */}
      <div className={classes.clusterItem}>
        <Checkbox
          checked={isClusterSelected(UNASSIGNED_CLUSTER)}
          onChange={(checked) => handleClusterToggle(UNASSIGNED_CLUSTER, checked)}
          disabled={unassignedHosts?.length === 0}
          info={
            unassignedHosts?.length === 0
              ? 'No unassigned hosts available. All hosts are currently assigned to clusters.'
              : ''
          }
          label={
            <div className={classes.clusterLabel}>
              <Text variant="caption1">Unassigned hosts</Text>
              {unassignedHosts?.length > 0 && (
                <span className={classes.badge}>{unassignedHosts?.length}</span>
              )}
            </div>
          }
        />
      </div>
      <Divider className={classes.divider} />

      {visibleClusters?.map((cluster) => (
        <div key={cluster?.id || cluster?.name} className={classes.clusterItem}>
          <Checkbox
            checked={isClusterSelected(cluster?.name)}
            onChange={(checked) => handleClusterToggle(cluster?.name, checked)}
            label={
              <div className={classes.clusterLabel}>
                <Text variant="caption1">{cluster?.name}</Text>
                {cluster?.hostlist?.length > 0 && (
                  <span className={classes.badge}>{cluster?.hostlist?.length}</span>
                )}
              </div>
            }
          />
        </div>
      ))}
    </div>
  )
}

export default ClusterList

const useStyles = makeStyles<Theme>((theme) => ({
  listContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  clusterItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5, 1, 1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginBottom: theme.spacing(0.5),
    '&:hover': {
      background: theme.palette.grey[50],
    },
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: colors.grey[200],
  },
  clusterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 14,
    height: 20,
    padding: theme.spacing(0, 0.75),
    background: colors.blue[700],
    color: theme.palette.common.white,
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
  },
}))
